// What "Replace saved version" is about to replace, read from the saved row
// itself. On 2026-10-12 one click replaced a colleague's newer 6-item version
// of a job with a 1-item version, with no confirmation and no word on what was
// being replaced (F48). The review names when the saved version was saved and
// what each version holds, and carries the saved row's own save token so the
// version described is exactly the version replaced: if a third save lands in
// between, the server refuses the overwrite instead.
import { sumScopeDisplayAmounts } from './quote-display-totals.js';

export interface OverwriteReview {
    savedAtLabel: string;
    savedItems: number;
    savedTotalLabel: string;
    localItems: number;
    localTotalLabel: string;
    token: string;
}

type LooseRow = Record<string, unknown>;

// HBCF, preliminaries and other administration rows are not items of work.
function isWorkItem(row: unknown): boolean {
    if (!row || typeof row !== 'object') return false;
    const item = row as LooseRow;
    if (item.isAdminItem === true || item.adminItem === true) return false;
    const code = String(item.code || '').trim().toUpperCase();
    if (code === 'PRELIMS' || code === 'HBCF' || code === 'HBCF-APP') return false;
    return !/^\s*hbcf\b/i.test(String(item.title || ''));
}

function money(scope: unknown[]): string {
    const total = sumScopeDisplayAmounts(scope);
    return `$${Number(total || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function savedAtLabel(savedAt: Date, now: Date): string {
    const time = savedAt.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }).toLowerCase();
    const sameDay = savedAt.toDateString() === now.toDateString();
    if (sameDay) return `${time} today`;
    const day = savedAt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
    return `${time} on ${day}`;
}

/**
 * The review for an overwrite, or null when the saved row cannot be read well
 * enough to describe it (no row, or no valid save time). Null means nothing
 * may be replaced.
 */
export function buildOverwriteReview({
    savedProject,
    localScope,
    now = new Date(),
}: {
    savedProject: unknown;
    localScope: unknown;
    now?: Date;
}): OverwriteReview | null {
    if (!savedProject || typeof savedProject !== 'object') return null;
    const saved = savedProject as LooseRow;
    const rawSavedAt = saved.updatedAt || saved.updated_at;
    if (!rawSavedAt || typeof rawSavedAt !== 'string') return null;
    const savedAt = new Date(rawSavedAt);
    if (Number.isNaN(savedAt.getTime())) return null;
    const savedScope = Array.isArray(saved.scope) ? saved.scope : [];
    const local = Array.isArray(localScope) ? localScope : [];
    return {
        savedAtLabel: savedAtLabel(savedAt, now),
        savedItems: savedScope.filter(isWorkItem).length,
        savedTotalLabel: money(savedScope),
        localItems: local.filter(isWorkItem).length,
        localTotalLabel: money(local),
        token: savedAt.toISOString(),
    };
}

export function itemCountLabel(count: number): string {
    return `${count} ${count === 1 ? 'item' : 'items'}`;
}
